import { formatAreaInHectares } from "../../utils/areaCalculation";

export const EMPTY_VALUE = "-";

/** Group labels follow the design: Plot A, Plot B, … */
export const getPlotGroupLabel = (index: number): string =>
  `Plot ${String.fromCodePoint(65 + index)}`;

/**
 * Formats a date the way the design shows it (YYYY/M/D).
 * @param value Raw date string from the trial record.
 * @returns The formatted date, or a dash when it cannot be parsed.
 */
export const formatPreviewDate = (value?: string | null): string => {
  if (!value) return EMPTY_VALUE;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;

  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
};

/**
 * Builds the "A ha (W x Hm)" summary the Plot Design section shows for each
 * plot group.
 *
 * `column_size` and `row_size` are the plot's width and height in **metres**,
 * not row/column counts — the same reading `calculateRegularPlotArea` uses.
 * Multiplying them gives m², which is reported in hectares.
 * @param plot The `plot` payload of a trial plot group.
 * @returns The formatted summary, or a dash when dimensions are missing.
 */
export const formatPlotDesign = (plot?: any): string => {
  if (!plot) return EMPTY_VALUE;

  const width = Number(plot?.column_size) || Number(plot?.plotWidth) || 0;
  const height = Number(plot?.row_size) || Number(plot?.plotHeight) || 0;

  if (!width || !height) return EMPTY_VALUE;

  return `${formatAreaInHectares(width * height)} (${width} x ${height}m)`;
};

/**
 * Formats a plot group's replicant count the way the Rates & Dosage headers do
 * ("+ 1 replicant"), or nothing when the group has none.
 * @param plotGroup A `trial_plots` entry.
 */
export const formatReplicants = (plotGroup?: any): string | undefined => {
  const count: number = Array.isArray(plotGroup?.replicants)
    ? plotGroup.replicants.length
    : 0;

  if (count === 0) return undefined;

  return `+ ${count} replicant${count === 1 ? "" : "s"}`;
};

/** One entry of the Plot Design section. */
export interface PlotDesignEntry {
  label: string;
  value: string;
  replicants?: string;
}

/**
 * Builds the Plot Design entries — one per plot group, with its replicant count.
 * @param trial The trial payload returned by `/trial/{id}`.
 */
export const buildPlotDesign = (trial: any): PlotDesignEntry[] => {
  const plots: any[] = Array.isArray(trial?.trial_plots)
    ? trial.trial_plots
    : [];

  return plots.map((plotGroup, index) => ({
    label: getPlotGroupLabel(index),
    value: formatPlotDesign(plotGroup?.plot),
    replicants: formatReplicants(plotGroup),
  }));
};

/**
 * Picks the protocol rows out of a `/protocols` response, whose envelope differs
 * by deployment.
 * @param data The response body.
 */
export const pickProtocolRows = (data: any): any[] => {
  if (Array.isArray(data?.protocols)) return data.protocols;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data)) return data;

  return [];
};

/**
 * The linked protocol's id. `/trials` and `/trial/{id}` do not agree on where it
 * lives, and an empty string is as good as absent.
 * @param trial The merged trial payload.
 * @param idFromName An id recovered from the protocol's name, if any.
 * @param protocolData The loaded protocol, once available.
 */
export const resolveLinkedProtocolId = (
  trial: any,
  idFromName = "",
  protocolData?: any
): string =>
  trial?.protocol_id ||
  trial?.protocolId ||
  trial?.protocol?.id ||
  idFromName ||
  protocolData?.id ||
  "";

/** Whether the payload already carries a protocol id of its own. */
export const hasOwnProtocolId = (trial: any): boolean =>
  Boolean(trial?.protocol_id || trial?.protocolId || trial?.protocol?.id);

/**
 * Whether a property field is the one a trial's `field_ids` entry refers to.
 * Ids match outright, or as a whole segment of a composite id — a substring
 * test would let `field-1` match `field-10`.
 * @param candidateId The `id` of a field returned by the property endpoint.
 * @param fieldId The id the trial references.
 */
export const matchesFieldId = (
  candidateId: string | number | null | undefined,
  fieldId: string | number
): boolean => {
  // Only strings and numbers are stringified: anything else has no meaningful
  // text form, and `String(someObject)` would compare "[object Object]".
  const candidate =
    typeof candidateId === "number" ? String(candidateId) : candidateId ?? "";
  const target = typeof fieldId === "number" ? String(fieldId) : fieldId;

  return candidate === target || candidate.split(/[:/]/).includes(target);
};

/**
 * Resolves a trial's field ids to the property's field names, dropping ids the
 * property does not know about.
 * @param fields The property's fields.
 * @param fieldIds The ids the trial references.
 */
export const buildFieldNames = (fields: any, fieldIds: any): string[] => {
  const known: any[] = Array.isArray(fields) ? fields : [];
  const ids: string[] = Array.isArray(fieldIds) ? fieldIds : [];

  return ids
    .map(
      (fieldId) =>
        known.find((field: any) => matchesFieldId(field?.id, fieldId))?.name
    )
    .filter((name: string | undefined): name is string => Boolean(name));
};

/**
 * The translation key for a trial's status tag.
 * @param trial The merged trial payload.
 */
export const getStatusTranslationKey = (trial: any): string => {
  if (trial?.completion === true) return "trialFilter.completed";
  if (trial?.is_exportable === true) return "trialPreview.status.ready";

  return "trialPreview.status.inProgress";
};

/**
 * The per-strip geometry the Rates & Dosage grid reads from
 * `newTrialState.stripsData`. Positional order matches the strip columns the
 * grid asks for, and `strip_id` is 1-based on the wire.
 * @param trial The merged trial payload.
 * @returns The strips data, or null when the trial is not a strip design.
 */
export const buildStripsData = (trial: any): any => {
  const strips = trial?.strip_design?.strips;

  if (trial?.strip_design?.is_strip !== true || !Array.isArray(strips)) {
    return null;
  }

  return {
    isStripMode: true,
    count: strips.length,
    areas: strips.map((strip: any) => strip?.area),
    stripDetails: strips.map((strip: any, index: number) => ({
      index: typeof strip?.strip_id === "number" ? strip.strip_id - 1 : index,
      area: strip?.area,
      stripLength: strip?.strip_length,
      stripWidth: strip?.strip_width,
    })),
  };
};

/** A trial designed as strips, per the trial-level flag or its plots. */
export const isStripTrial = (trial: any): boolean => {
  if (trial?.strip_design?.is_strip === true) return true;

  const plots: any[] = Array.isArray(trial?.trial_plots)
    ? trial.trial_plots
    : [];

  return plots.some(
    (plotGroup) =>
      plotGroup?.plot?.is_strip === true ||
      plotGroup?.plot?.isStripPlot === true
  );
};

/** Formats a metre measurement the way the Summary step's strip table does. */
const formatMeters = (value?: number | null): string =>
  typeof value === "number" && Number.isFinite(value)
    ? `${value.toFixed(1)} m`
    : EMPTY_VALUE;

/** One row of the Strip Design table. */
export interface StripDesignRow {
  label: string;
  length: string;
  width: string;
  area: string;
}

/**
 * Builds the Strip Design rows — one per strip, mirroring the Summary step's
 * strip table (Strip / Length / Width / Area).
 *
 * `strip_design.strips` is the authoritative record. Trials saved before it was
 * populated fall back to the per-strip `trial_plots` entries, which carry the
 * same measurements on the plot itself.
 * @param trial The trial payload returned by `/trial/{id}`, with `trial_plots`
 * still unmerged so every strip is visible.
 */
export const buildStripDesign = (trial: any): StripDesignRow[] => {
  const strips: any[] = Array.isArray(trial?.strip_design?.strips)
    ? trial.strip_design.strips
    : [];

  if (strips.length > 0) {
    return strips.map((strip, index) => ({
      label: String(index + 1),
      length: formatMeters(strip?.strip_length),
      width: formatMeters(strip?.strip_width),
      area: formatAreaInHectares(strip?.area),
    }));
  }

  const stripPlots: any[] = (
    Array.isArray(trial?.trial_plots) ? trial.trial_plots : []
  ).filter(
    (plotGroup: any) =>
      plotGroup?.plot?.is_strip === true ||
      plotGroup?.plot?.isStripPlot === true
  );

  return stripPlots.map((plotGroup, index) => ({
    label: String(index + 1),
    length: formatMeters(plotGroup?.plot?.stripLength),
    width: formatMeters(plotGroup?.plot?.stripWidth),
    area: formatAreaInHectares(plotGroup?.plot?.stripArea),
  }));
};
